/**
 * Shows one screen at a time inside the menu root, with at most one modal over it, and owns
 * the keyboard rules common to all of them: Esc = back (or cancel the modal), arrows move focus.
 * Enter, Space and Tab are the browser's own, because every control is a real button or input;
 * the one addition is that Enter in a text field moves on to the next control.
 */

import type { Modal, Screen } from './context';
import { el, Timers } from './dom';
import type { Direction } from './focusNav';
import { focusFirst, moveFocus } from './focusNav';

const ARROWS: Readonly<Record<string, Direction>> = {
  ArrowUp: 'up',
  ArrowDown: 'down',
  ArrowLeft: 'left',
  ArrowRight: 'right',
};

function isTextField(node: Element | null): boolean {
  return node !== null && node.tagName === 'INPUT' && (node as HTMLInputElement).type === 'text';
}

export class Router {
  /** Timeouts of the screen on show; cleared on every change of screen. */
  readonly timers = new Timers();
  private readonly stage: HTMLElement;
  private readonly modalLayer: HTMLElement;
  private screen: Screen | null = null;
  private modal: Modal | null = null;
  private focusBeforeModal: HTMLElement | null = null;

  constructor(host: HTMLElement) {
    this.stage = el('div', 'menu-stage');
    this.modalLayer = el('div', 'menu-modal-layer menu-hidden');
    host.append(this.stage, this.modalLayer);
  }

  /**
   * Takes the screen's factory, not the screen: the old screen and its timers must be gone
   * BEFORE the new one is built, or the timers the new screen starts while it is being built
   * (a typewriter's first tick, the debrief's score lines) would be cleared along with them.
   */
  show(make: () => Screen): void {
    this.closeModal();
    this.dropScreen();
    const next = make();
    this.screen = next;
    this.stage.replaceChildren(next.el);
    this.focusInto(next.el, next.initialFocus?.() ?? null);
  }

  /** Remove whatever is up (the menus are being hidden or disposed). */
  clear(): void {
    this.closeModal();
    this.dropScreen();
    this.stage.replaceChildren();
  }

  openModal(modal: Modal): void {
    this.closeModal();
    const active = document.activeElement;
    this.focusBeforeModal = active !== null && this.stage.contains(active) ? (active as HTMLElement) : null;
    this.modal = modal;
    // The screen beneath must not take Tab or clicks while a decision is pending.
    this.stage.inert = true;
    this.modalLayer.replaceChildren(modal.el);
    this.modalLayer.classList.remove('menu-hidden');
    this.focusInto(modal.el, modal.initialFocus?.() ?? null);
  }

  closeModal(): void {
    if (this.modal === null) return;
    this.modal = null;
    this.stage.inert = false;
    this.modalLayer.classList.add('menu-hidden');
    this.modalLayer.replaceChildren();
    const before = this.focusBeforeModal;
    this.focusBeforeModal = null;
    if (before !== null && before.isConnected) before.focus();
    else if (this.screen !== null) focusFirst(this.screen.el);
  }

  /** The one keydown handler for the menus. Returns true when the key was consumed. */
  handleKey(ev: KeyboardEvent): boolean {
    if (ev.altKey || ev.ctrlKey || ev.metaKey) return false;
    if (ev.key === 'Escape') {
      if (this.modal !== null) this.modal.cancel();
      else this.screen?.back();
      return true;
    }
    const scope = this.modal?.el ?? this.screen?.el ?? null;
    if (scope === null) return false;
    // Enter in a text field has nothing to submit: it moves on to the next control, like a form.
    if (ev.key === 'Enter') return isTextField(document.activeElement) && moveFocus(scope, 'down');
    const dir = ARROWS[ev.key];
    return dir !== undefined && moveFocus(scope, dir);
  }

  private dropScreen(): void {
    this.timers.clear();
    this.screen?.dispose?.();
    this.screen = null;
  }

  private focusInto(scope: HTMLElement, preferred: HTMLElement | null): void {
    if (preferred !== null && preferred.isConnected) preferred.focus();
    else if (!focusFirst(scope)) (document.activeElement as HTMLElement | null)?.blur?.();
  }
}
